/**
 * useFileUpload — Handles PDF, EPUB, and TXT/Markdown file processing.
 *
 * Manages upload state (uploadingPdf, uploadingEpub) and provides handlers
 * that validate files and call the appropriate store functions to create articles.
 *
 * For PDFs > 100 pages, opens PdfGateModal to let the user select a page range
 * before uploading. Tier caps: Free=200 pages, Trial/Pro=500 pages, Max=unlimited.
 *
 * Also provides refs for hidden file inputs and their onChange event handlers
 * (legacy compatibility — UploadModal has its own drag-drop zone).
 *
 * @param {Object}  options
 * @param {string|null} options.listId  - Target list ID (null = Inbox)
 */

import { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { pdfjs } from 'react-pdf';
import pdfjsWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { showErrorAlert, ErrorCategory } from '../lib/errorHandler';
import { useFeatureAccess } from './useFeatureAccess';
import { useSubscription } from './useSubscription';
import { openUsageModal, openModal, closeModal } from '../store/ui$';
import { supabase } from '../lib/supabase';
import {
  createPdfArticle,
  createEpubArticle,
  createTextFileArticle,
  createAudioArticle,
} from '../store';

// PDF.js worker — bundled via Vite ?url (same URL as PdfViewer.jsx; idempotent double-set is fine)
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerUrl;

/**
 * Get page count from a PDF file using PDF.js.
 * Returns null if detection fails (large/encrypted PDFs may fail to parse).
 */
async function getPdfPageCount(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdf.numPages;
    pdf.destroy();
    return numPages;
  } catch (err) {
    console.warn('[PDF] Page count detection failed:', err);
    return null;
  }
}

/** Tier → max pages per upload. Trial users are stored as tier='pro' in DB. */
function getTierPageCap(tier) {
  if (tier === 'max') return Infinity;
  if (tier === 'pro') return 500;
  return 200; // free (default)
}

/**
 * Module-level gate state — stores the pending PDF file and the upload function
 * from whichever hook instance opened the gate. Keyed outside React state so that
 * GlobalUploadModal's PdfGateModal callbacks can reach the correct doUploadFn
 * even when the gate was triggered from a different instance (ArticleImporter).
 *
 * Only one gate can be active at a time (enforced by ui$.activeModal === 'pdfGate').
 */
let _pendingGateState = null; // { file, numPages, doUploadFn }

export default function useFileUpload({ listId }) {
  const { t } = useTranslation('articles');
  const navigate = useNavigate();
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingEpub, setUploadingEpub] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const { subscription } = useSubscription();
  const { allowed: pdfAllowed, usageCount: pdfUsageCount, limit: pdfLimit, refresh: refreshPdfUsage } = useFeatureAccess('pdf_upload');

  // Refs for hidden file inputs
  const pdfInputRef = useRef(null);
  const epubInputRef = useRef(null);
  const textFileInputRef = useRef(null);
  const audioInputRef = useRef(null);

  // Resolve target list: 'all' view means add to Inbox (null)
  const targetListId = listId === 'all' ? null : listId;

  const tierPageCap = useMemo(() => getTierPageCap(subscription?.tier), [subscription?.tier]);

  // ── Internal: actual PDF upload execution ──────────────────────────────────

  const doUpload = useCallback(async (file, pageRanges) => {
    setUploadingPdf(true);
    toast.info(t('input.uploading')); // shown here so gate-path PDFs don't see it before the modal
    try {
      await createPdfArticle(file, targetListId, pageRanges);
      toast.success(t('pdfUpload.uploaded', { defaultValue: 'PDF uploaded. Processing will begin shortly.' }), {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });

      refreshPdfUsage();
    } catch (err) {
      showErrorAlert(`Failed to upload PDF: ${err.message}`, err, { component: 'FileUpload', action: 'pdf_upload', category: ErrorCategory.IMPORT });
    } finally {
      setUploadingPdf(false);
    }
  }, [targetListId, refreshPdfUsage, t, navigate]);

  // ── Gate: confirm (user selected a range and clicked Process) ──────────────

  const confirmPdfGate = useCallback(async (startPage, endPage) => {
    // Read and immediately clear the module-level gate state.
    // This works regardless of which hook instance (GlobalUploadModal or ArticleImporter)
    // originally opened the gate, because _pendingGateState stores the doUploadFn that
    // captures the correct targetListId for that instance.
    const state = _pendingGateState;
    if (!state) return;
    _pendingGateState = null;
    closeModal();

    await state.doUploadFn(state.file, `${startPage}-${endPage}`);

    // Next-segment nudge: if more pages remain, suggest uploading the next chunk
    if (endPage < state.numPages) {
      const nextFrom = endPage + 1;
      const nextTo = Math.min(endPage + (tierPageCap === Infinity ? state.numPages : tierPageCap), state.numPages);
      toast.info(
        t('pdfGate.nextSegmentToast', {
          from: startPage,
          to: endPage,
          nextFrom,
          nextTo,
          defaultValue: `Pages ${startPage}–${endPage} submitted. Process pages ${nextFrom}–${nextTo}?`,
        }),
        {
          duration: 8000,
          action: {
            label: t('pdfGate.uploadNext', { defaultValue: 'Upload next' }),
            onClick: async () => {
              // Re-register gate state for the next segment, preserving the
              // original doUploadFn (and its targetListId closure)
              _pendingGateState = { file: state.file, numPages: state.numPages, doUploadFn: state.doUploadFn };
              const { data: { user } } = await supabase.auth.getUser();
              let creditBalance = 0;
              if (user) {
                const { data } = await supabase.rpc('reedle_subscription_balance', { p_user_id: user.id });
                creditBalance = data?.total_balance ?? 0;
              }
              openModal('pdfGate', {
                fileName: state.file.name.replace(/\.pdf$/i, ''),
                numPages: state.numPages,
                tierCap: tierPageCap,
                creditBalance,
                initialFrom: nextFrom,
                initialTo: nextTo,
              });
            },
          },
        }
      );
    }
  }, [tierPageCap, t]); // pendingPdfFile/doUpload removed — read from module-level _pendingGateState

  // ── Gate: cancel ───────────────────────────────────────────────────────────

  const cancelPdfGate = useCallback(() => {
    _pendingGateState = null;
    closeModal();
  }, []);

  // ── PDF ────────────────────────────────────────────────────────────────────

  const handlePdfUploadFile = useCallback(async (file) => {
    // Check PDF upload tier limit
    if (!pdfAllowed) {
      openUsageModal('plan', 'pdf_upload');
      return;
    }
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      showErrorAlert('Please select a PDF file', null, { component: 'FileUpload', action: 'pdf_validation', category: ErrorCategory.IMPORT });
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showErrorAlert('PDF must be less than 20MB', null, { component: 'FileUpload', action: 'pdf_size_validation', category: ErrorCategory.IMPORT });
      return;
    }

    // Detect page count via PDF.js (toast fires later, inside doUpload, so gated
    // PDFs don't show "Uploading..." before the modal opens)
    const numPages = await getPdfPageCount(file);

    // Gate: show modal for PDFs > 100 pages so user can pick range and see cost
    if (numPages && numPages > 100) {
      const { data: { user } } = await supabase.auth.getUser();
      let creditBalance = 0;
      if (user) {
        const { data } = await supabase.rpc('reedle_subscription_balance', { p_user_id: user.id });
        creditBalance = data?.total_balance ?? 0;
      }
      // Register gate state at module level so GlobalUploadModal's PdfGateModal
      // callbacks can reach this instance's doUploadFn (which captures targetListId)
      _pendingGateState = { file, numPages, doUploadFn: doUpload };
      openModal('pdfGate', {
        fileName: file.name.replace(/\.pdf$/i, ''),
        numPages,
        tierCap: tierPageCap,
        creditBalance,
      });
      return; // upload resumes via confirmPdfGate
    }

    // Small PDF (≤ 100 pages) or page detection failed: upload directly, no gate needed
    await doUpload(file, null);
  }, [pdfAllowed, tierPageCap, doUpload, t]);

  // Event wrapper for hidden <input type="file">
  const handlePdfUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    handlePdfUploadFile(file);
  }, [handlePdfUploadFile]);

  // ── EPUB ───────────────────────────────────────────────────────────────────

  const handleEpubUploadFile = useCallback(async (file) => {
    if (file.type !== 'application/epub+zip' && !file.name.toLowerCase().endsWith('.epub')) {
      showErrorAlert('Please select an EPUB file', null, { component: 'FileUpload', action: 'epub_validation', category: ErrorCategory.IMPORT });
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      showErrorAlert('EPUB must be less than 50MB', null, { component: 'FileUpload', action: 'epub_size_validation', category: ErrorCategory.IMPORT });
      return;
    }
    toast.info(t('input.uploading'));
    setUploadingEpub(true);
    try {
      await createEpubArticle(file, targetListId);
      toast.success(t('epubUpload.uploaded', { defaultValue: 'EPUB uploaded — converting' }), {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
    } catch (err) {
      showErrorAlert('Failed to upload EPUB: ' + err.message, err, { component: 'FileUpload', action: 'epub_upload', category: ErrorCategory.IMPORT });
    } finally {
      setUploadingEpub(false);
    }
  }, [targetListId, t, navigate]);

  const handleEpubUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    handleEpubUploadFile(file);
  }, [handleEpubUploadFile]);

  // ── TXT / Markdown ─────────────────────────────────────────────────────────

  const handleTextFileUploadFile = useCallback((file) => {
    const validExtensions = ['.txt', '.md', '.markdown'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    if (!hasValidExtension) {
      showErrorAlert('Please select a TXT or Markdown file', null, { component: 'FileUpload', action: 'text_validation', category: ErrorCategory.IMPORT });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showErrorAlert('Text file must be less than 5MB', null, { component: 'FileUpload', action: 'text_size_validation', category: ErrorCategory.IMPORT });
      return;
    }
    toast.info(t('input.uploading'));
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        try {
          createTextFileArticle({ fileName: file.name, content, listId: targetListId });
          toast.success(t('textUpload.uploaded', { defaultValue: 'Text file uploaded successfully.' }), {
            action: { label: 'View All', onClick: () => navigate('/all') },
            duration: 4000,
          });
        } catch (err) {
          showErrorAlert('Failed to import text file: ' + err.message, err, { component: 'FileUpload', action: 'text_import', category: ErrorCategory.IMPORT });
        }
      }
    };
    reader.onerror = () => {
      showErrorAlert('Failed to read file', null, { component: 'FileUpload', action: 'text_read', category: ErrorCategory.IMPORT });
    };
    reader.readAsText(file);
  }, [targetListId, t, navigate]);

  const handleTextFileUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    handleTextFileUploadFile(file);
  }, [handleTextFileUploadFile]);

  // ── Audio ──────────────────────────────────────────────────────────────────

  const handleAudioUploadFile = useCallback(async (file) => {
    const validExtensions = ['.mp3', '.m4a', '.wav', '.ogg'];
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    const hasValidMime = file.type.startsWith('audio/');
    if (!hasValidExtension && !hasValidMime) {
      showErrorAlert('Please select an audio file (MP3, M4A, WAV, or OGG)', null, {
        component: 'FileUpload', action: 'audio_validation', category: ErrorCategory.IMPORT,
      });
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showErrorAlert(t('audioUpload.tooLarge', { defaultValue: 'Audio file must be less than 25 MB. Compress to MP3 at 128 kbps for better results.' }), null, {
        component: 'FileUpload', action: 'audio_size_validation', category: ErrorCategory.IMPORT,
      });
      return;
    }
    toast.info(t('input.uploading'));
    setUploadingAudio(true);
    try {
      await createAudioArticle(file, targetListId);
      toast.success(t('audioUpload.uploaded', { defaultValue: 'Audio uploaded. Transcribing will begin shortly.' }), {
        action: { label: 'View All', onClick: () => navigate('/all') },
        duration: 4000,
      });
    } catch (err) {
      showErrorAlert('Failed to upload audio: ' + err.message, err, {
        component: 'FileUpload', action: 'audio_upload', category: ErrorCategory.IMPORT,
      });
    } finally {
      setUploadingAudio(false);
    }
  }, [targetListId, t, navigate]);

  const handleAudioUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    handleAudioUploadFile(file);
  }, [handleAudioUploadFile]);

  // ── Unified multi-file processor (for drag-drop) ───────────────────────────

  const processDroppedFiles = useCallback((files) => {
    if (uploadingPdf || uploadingEpub || uploadingAudio) return;
    const fileList = Array.from(files);
    if (fileList.length > 10) {
      // toast handled by caller if needed
    }
    const batch = fileList.slice(0, 10);
    for (const file of batch) {
      const name = file.name.toLowerCase();
      const type = file.type;
      if (type === 'application/pdf' || name.endsWith('.pdf')) {
        handlePdfUploadFile(file);
      } else if (type === 'application/epub+zip' || name.endsWith('.epub')) {
        handleEpubUploadFile(file);
      } else if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.markdown')) {
        handleTextFileUploadFile(file);
      } else if (file.type.startsWith('audio/') || ['.mp3', '.m4a', '.wav', '.ogg'].some(ext => name.endsWith(ext))) {
        handleAudioUploadFile(file);
      } else {
        showErrorAlert(t('uploadModal.unsupportedFormat'), null, {
          component: 'FileUpload', action: 'drop_file_type', category: ErrorCategory.IMPORT,
        });
      }
    }
  }, [uploadingPdf, uploadingEpub, uploadingAudio, handlePdfUploadFile, handleEpubUploadFile, handleTextFileUploadFile, handleAudioUploadFile, t]);

  return {
    // File handlers (accept File objects)
    handlePdfUploadFile,
    handleEpubUploadFile,
    handleTextFileUploadFile,
    handleAudioUploadFile,
    // Event handlers (for <input type="file"> onChange)
    handlePdfUpload,
    handleEpubUpload,
    handleTextFileUpload,
    handleAudioUpload,
    // Drag-drop batch processor
    processDroppedFiles,
    // State
    uploadingPdf,
    uploadingEpub,
    uploadingAudio,
    // Refs for hidden inputs
    pdfInputRef,
    epubInputRef,
    textFileInputRef,
    audioInputRef,
    // PDF upload limit info
    pdfUsageCount,
    pdfLimit,
    // PDF gate callbacks (used by PdfGateModal via GlobalUploadModal)
    confirmPdfGate,
    cancelPdfGate,
  };
}
